import { useMutation } from '@apollo/client';
import { Theme } from '@busybusy/webapp-react-ui';
import { MEMBER_LOCK_CREATE_QUERY, MEMBER_LOCK_UPDATE_MUTATION } from 'apollo/mutations/member-lock-mutations';
import { MEMBER_LOCKS_QUERY } from 'apollo/queries/member-lock-queries';
import { useToastDispatch } from 'contexts/ToastContext';
import { useApolloPaging } from 'hooks';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import ICursorable from 'types/Cursorable';
import IMemberLock, { IMemberLockCreateInput, IMemberLockUpdateInput } from 'types/MemberLock';
import { dateTimeFromUtcISO, isoTimeStampUtc } from 'utils/dateUtils';
import { inhibitsActions } from 'utils/memberLockUtils';
import { uuid } from 'utils/uuidUtils';

export default function useMemberLock() {
  const [createMemberLockMutation] = useMutation<{ createMemberLock: IMemberLock }>(MEMBER_LOCK_CREATE_QUERY);
  const [updateMemberLockMutation] = useMutation<{ updateMemberLock: IMemberLock }>(MEMBER_LOCK_UPDATE_MUTATION);
  const { getAll } = useApolloPaging();
  const toastDispatch = useToastDispatch();

  async function createLock(date: DateTime, memberId: string) {
    const item: IMemberLockCreateInput = {
      id: uuid(),
      memberId,
      effectiveDate: date.endOf('day').toISO(),
      createdOn: isoTimeStampUtc(),
    };

    let newLock;

    try {
      const result = await createMemberLockMutation({ variables: { lock: item } });
      newLock = result.data?.createMemberLock;
    } catch {
      toastDispatch({
        type: 'OPEN',
        payload: {
          label: t('Request failed. Check for open time entries before the selected lock date.'),
          theme: Theme.DANGER,
        },
      });
    }

    return newLock;
  }

  async function editLock(lock: Pick<IMemberLock, 'id'>, date: DateTime) {
    const item: IMemberLockUpdateInput = {
      id: lock.id,
      effectiveDate: date.endOf('day').toISO(),
    };

    let newLock;

    try {
      const result = await updateMemberLockMutation({ variables: { lock: item } });
      newLock = result.data?.updateMemberLock;
    } catch {
      toastDispatch({
        type: 'OPEN',
        payload: {
          label: t('Request failed. Check for open time entries before the selected lock date.'),
          theme: Theme.DANGER,
        },
      });
    }

    return newLock;
  }

  async function deleteLock(lock: Pick<IMemberLock, 'id'>) {
    const item: IMemberLockUpdateInput = {
      id: lock.id,
      effectiveDate: null,
    };

    const result = await updateMemberLockMutation({ variables: { lock: item } });
    const newLock = result.data?.updateMemberLock;
    return newLock;
  }

  async function inhibitsActionsForMembers(memberIds: string[], date: DateTime) {
    const locks = await getAll<IMemberLock & ICursorable>('memberLocks', {
      query: MEMBER_LOCKS_QUERY,
      variables: {
        first: 500,
        filter: {
          memberId: { contains: memberIds },
        },
      },
      fetchPolicy: 'network-only',
    });

    return locks.some((l) => {
      const isInhibited = inhibitsActions(dateTimeFromUtcISO(l.effectiveDate!), date);
      return isInhibited;
    });
  }

  return { createLock, editLock, deleteLock, inhibitsActionsForMembers };
}
